import * as babylon from "@babylonjs/core";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import { DataServerManager } from "../DataServer/DataServerManager";

import * as components from "./components";

/**
 * Plain `{x, y, z}` counterpart to Babylon's `Vector3`. Used in
 * serializable state where reaching into Babylon internals would couple
 * persistence and tests to runtime objects.
 */
export interface Vector3Plain {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface BabylonState {
  entities: Record<schema.EntityID, Entity>;
  sceneConfig: SceneConfig;
  input: BabylonInput;
  readonly context: BabylonContext;
}

/**
 * App-controlled input state that systems write and Babylon consumes.
 * Plain-shaped so persistence and tests don't reach into Babylon
 * internals.
 */
export interface BabylonInput {
  /**
   * Summing accumulator for third-person camera input. DOM event
   * handlers (`handleOrbitInput`, `handlePanInput`, `handleZoomInput`)
   * write per-event deltas at the same sensitivity calibrations the
   * codebase ships today; `updateCamera`'s third-person branch reads
   * the accumulator each frame, applies it to `babylonCamera.{alpha,
   * beta, radius}` and `camera.focusPoint`, then zeroes every consumed
   * field. The accumulator decouples pointer-event rate from frame
   * rate — every event contributes exactly once per frame to camera
   * state. Orbit input continues to flow through Babylon's
   * `ArcRotateCameraPointersInput`, so these fields stay at zero in
   * orbit mode.
   */
  cameraDeltas: CameraDeltas;
}

/**
 * Per-frame input deltas summed by DOM handlers and consumed by
 * `updateCamera`. Each field maps to one third-person gesture.
 */
export interface CameraDeltas {
  /** Orbit drag, alpha axis (horizontal). Radians. */
  alpha: number;
  /** Orbit drag, beta axis (vertical). Radians. */
  beta: number;
  /** Shift+drag pan along world X. Data-space units. */
  panX: number;
  /** Shift+drag pan along world Z. Data-space units. */
  panZ: number;
  /** Wheel scroll. Sign matches Babylon's radius convention. */
  zoom: number;
  /** Shift+wheel elevation. Adds to `camera.focusPoint.y`. Data-space units. */
  elevateY: number;
}

/**
 * Zero value for `CameraDeltas`. Used for first-frame init in
 * `BabylonManager.initState` and for the mode-flip reset in
 * `updateSceneConfig` (see those sites for the rationale). Spread into
 * a fresh object at each use site — `Readonly` enforces this at the
 * type level, but callers still need a mutable copy to write to.
 */
export const ZERO_CAMERA_DELTAS: Readonly<CameraDeltas> = {
  alpha: 0,
  beta: 0,
  panX: 0,
  panZ: 0,
  zoom: 0,
  elevateY: 0,
};

export interface BabylonContext {
  readonly systemSchedules: Record<string, SystemSchedule>;
  readonly dataServer: DataServerManager;
  readonly engine: babylon.Engine;
  readonly scene: babylon.Scene;
  readonly callbacks: Callbacks;
  readonly canvas: HTMLCanvasElement;
}

export interface SystemSchedule {
  dispose: () => void;
}

export interface Callbacks {
  onLoadingStatusChanged: (isLoading: boolean) => void;
}

export interface BabylonTheme {
  backgroundColor: babylon.Color4;
}

export type Entity = {
  id: schema.EntityID;
} & Partial<{
  position: components.Position;
  mesh: components.Mesh;
  path: components.Path;
  vectorField: components.VectorField;
}>;

/**
 * A special singleton entity whose components are only needed once.
 */
export type SceneConfig = Partial<{
  grid: components.singletons.Grid;
  camera: components.singletons.Camera;
  time: bigint;
  /**
   * Bumped whenever the entity set or any entity's bounded mesh
   * changes (`updateEntities` for adds/removes/mesh rescales, and
   * `reloadStalePaths` when a new path mesh is built). Read by
   * `computeThirdPersonZoom`'s cache to decide whether to recompute
   * the scene-union envelope; a stable generation means the cached
   * result is still valid.
   */
  entityGeneration: number;
  /**
   * Cached result of `computeThirdPersonZoom`, valid for the
   * `entityGeneration` it was computed against. Mismatched generation
   * triggers a recompute on the next read.
   */
  thirdPersonZoomCache: {
    value: { min: number; max: number };
    generation: number;
  };
}>;
